import type { SettlementGridBounds } from './settlement-projection';

export interface SettlementConstructionAnchor {
  /** Grid coordinates for the same projection as the authoritative building footprint. */
  x: number;
  y: number;
  /** Rotated normalized coordinates inside the effective, already rotated API bounds. */
  u: number;
  v: number;
}

export interface SettlementConstructionLayout {
  workers: SettlementConstructionAnchor[];
  supplies: SettlementConstructionAnchor;
  finishingRig: SettlementConstructionAnchor;
  /** Unit grid vector pointing out of the entrance; project it before deriving a screen angle. */
  entranceHeading: { x: number; y: number };
}

/** Temporary work equipment sits on the camera-facing corner of its own plot.
 * Unlike the entrance/crew, it must not disappear behind the completed silhouette.
 * Choose by projected ground depth, never by raising its render layer above the city.
 */
export function planSettlementFinishingWorksite(
  bounds: SettlementGridBounds,
  project: (point: { x: number; y: number }) => { x: number; y: number },
): SettlementConstructionAnchor | undefined {
  if (!planSettlementConstructionLayout(bounds, 0, 0)) return undefined;
  const candidates = [
    [0.21, 0.21],
    [0.79, 0.21],
    [0.79, 0.79],
    [0.21, 0.79],
  ] as const;
  const anchors = candidates.map(([u, v]) => ({
    x: bounds.minX + u * (bounds.maxX - bounds.minX),
    y: bounds.minY + v * (bounds.maxY - bounds.minY),
    u,
    v,
  }));
  const projected = anchors.map((anchor) => ({ anchor, point: project(anchor) }));
  if (projected.some(({ point }) => !Number.isFinite(point.x) || !Number.isFinite(point.y)))
    return undefined;
  return projected.reduce((front, next) => (next.point.y > front.point.y ? next : front)).anchor;
}

/** Local presentation anchors only; this never rotates or changes the server-owned footprint. */
export function planSettlementConstructionLayout(
  bounds: SettlementGridBounds,
  orientation: number,
  workerCount = 3,
): SettlementConstructionLayout | undefined {
  const { minX, minY, maxX, maxY } = bounds;
  if (
    ![minX, minY, maxX, maxY].every(Number.isFinite) ||
    maxX <= minX ||
    maxY <= minY ||
    !Number.isInteger(orientation) ||
    orientation < 0 ||
    orientation > 3
  )
    return undefined;

  const anchor = (u: number, v: number): SettlementConstructionAnchor => {
    for (let turn = 0; turn < orientation; turn++) [u, v] = [1 - v, u];
    return { x: minX + u * (maxX - minX), y: minY + v * (maxY - minY), u, v };
  };
  const count = Number.isFinite(workerCount)
    ? Math.max(0, Math.min(3, Math.floor(workerCount)))
    : 0;
  const workerAnchors = [anchor(0.32, 0.84), anchor(0.5, 0.78), anchor(0.68, 0.84)];
  const headings = [
    { x: 0, y: 1 },
    { x: -1, y: 0 },
    { x: 0, y: -1 },
    { x: 1, y: 0 },
  ];
  return {
    workers: workerAnchors.slice(0, count),
    supplies: anchor(0.76, 0.65),
    finishingRig: anchor(0.24, 0.65),
    entranceHeading: headings[orientation]!,
  };
}

/**
 * Fit an upright detail's authored ground extent inside both target ground dimensions.
 * The aspect ratio is ground depth / ground width, not the whole upright image's ratio.
 * Return the rendered ground width; the caller converts it through the measured source ground
 * width into ONE image scale. Never use this result to squash or shear upright artwork.
 */
export function fitSettlementConstructionDetailWidth(
  targetGroundWidth: number,
  targetGroundDepth: number,
  authoredGroundDepthToWidth: number,
): number {
  if (
    ![targetGroundWidth, targetGroundDepth, authoredGroundDepthToWidth].every(
      (value) => Number.isFinite(value) && value > 0,
    )
  )
    return 0;
  return Math.min(targetGroundWidth, targetGroundDepth / authoredGroundDepthToWidth);
}
